import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { isApolloError } from '@apollo/client';
import { APIResponse } from 'spw-redux/types';
import { NAME_TAKEN_MESSAGE } from 'spw-constants';
import { showToastMessage, handleError } from '../UI/messages/actions';
import {
  getAllTeams as getAllTeamsApi,
  createTeam as createTeamApi,
  updateTeam as updateTeamApi,
  deleteTeam as deleteTeamApi,
  addUserToTeam as addUserToTeamApi,
  removeUserFromTeam as removeUserFromTeamApi,
  joinTeam as joinTeamApi,
  issueRequestToJoinTeam as issueRequestToJoinTeamApi,
  leaveTeam as leaveTeamApi,
  getTeam as getTeamApi,
  getTeamsForProject,
  getProjectsForTeam as getProjectsForTeamApi,
  getUsersInTeam as getUsersInTeamApi,
  getTeamsForPopoverGivenUser as getTeamsForPopoverGivenUserApi,
  getTeamsForPopoverGivenClient as getTeamsForPopoverGivenClientApi,
} from '../../../api/teams';
import { actions, actionNames } from './actions';
import {
  Team,
  TeamListItemVM,
  TeamDetailVM,
  TeamSlimForList,
  TeamMemberRole,
  TeamMemberListVM,
} from './types';
import { getTeamsForSelectedUser } from '../users/actions';
import { selectTeam } from './selectors';

const TOAST_ERROR_MESSAGE =
  'There was a problem. Please contact your Organization Admin.';

const getTeamMemberListVM = ({
  users,
  teamAdmins,
}: Pick<Team, 'users' | 'teamAdmins'>): TeamMemberListVM[] => {
  return users?.map(
    ({
      id,
      name,
      numberOfProjects,
      numberOfTeams,
      officeLocation,
      professionalRole,
      hasAvatar,
      thumbnailS3Path,
      isActive,
    }) => ({
      id,
      name,
      numberOfProjects,
      numberOfTeams,
      officeLocation,
      professionalRole,
      hasAvatar,
      thumbnailS3Path,
      isActive,
      role: teamAdmins?.map((admin) => admin.id).includes(id)
        ? TeamMemberRole.admin
        : TeamMemberRole.user,
    })
  );
};

const getTeamDetailVM = (team: Team): TeamDetailVM => ({
  ...team,
  teamAdmins: team.teamAdmins?.map((admin) => ({
    ...admin,
    role: TeamMemberRole.admin,
  })),
  users: getTeamMemberListVM(team),
});

const getTeamListVM = (
  teams: TeamSlimForList[],
  action?: string
): TeamListItemVM[] =>
  teams.map((team) => ({ ...team, action } as TeamListItemVM));

function* getTeams(action: ReturnType<typeof actions.getTeams>) {
  try {
    const response: APIResponse<typeof getAllTeamsApi> = yield call(
      getAllTeamsApi,
      action.payload
    );

    yield put(
      actions.getTeamsSucceeded({
        teams: getTeamListVM(response.data.results.teams),
        total: response.data.results.total,
        shouldAppend: action.payload.page > 1,
      })
    );
  } catch (e) {
    yield put(actions.getTeamsFailed());
    yield put(handleError(e));
  }
}

function* getTeam(action: ReturnType<typeof actions.getTeam>) {
  try {
    const response: APIResponse<typeof getTeamApi> = yield call(
      getTeamApi,
      action.payload.id
    );

    yield put(actions.getTeamSucceeded(getTeamDetailVM(response.data.team)));
  } catch (e) {
    yield put(actions.getTeamFailed());
    yield put(handleError(e));
  }
}

function* createTeam(action: ReturnType<typeof actions.createTeam>) {
  try {
    const {
      name,
      permissionSettings,
      teamAdmin1,
      teamAdmin2,
    } = action.payload.form;
    const response: APIResponse<typeof createTeamApi> = yield call(
      createTeamApi,
      {
        name,
        teamAdmins: [teamAdmin1, teamAdmin2]
          .filter((admin) => admin)
          .map((admin) => admin.id),
        requiresRequestToJoin: permissionSettings === 'requiresRequestToJoin',
      }
    );
    yield call(action.payload.onSuccess, response.data.team.id);
  } catch (e) {
    let becauseNameTaken = false;

    if (isApolloError(e)) {
      if (e.graphQLErrors[0]?.message === NAME_TAKEN_MESSAGE) {
        becauseNameTaken = true;
      }
    }

    yield put(actions.createTeamFailed(becauseNameTaken));

    if (!becauseNameTaken) {
      yield put(handleError(e));
    }
  }
}

function* updateTeam(action: ReturnType<typeof actions.updateTeam>) {
  try {
    const { form, onSuccess } = action.payload;
    const response: APIResponse<typeof updateTeamApi> = yield call(
      updateTeamApi,
      form
    );
    yield put(actions.updateTeamSucceeded(getTeamDetailVM(response.data.team)));
    if (onSuccess) yield call(onSuccess);
  } catch (e) {
    let becauseNameTaken = false;

    if (isApolloError(e)) {
      if (e.graphQLErrors[0]?.message === NAME_TAKEN_MESSAGE) {
        becauseNameTaken = true;
      }
    }

    yield put(actions.updateTeamFailed(becauseNameTaken));

    if (!becauseNameTaken) {
      yield put(handleError(e));
    }
  }
}

function* deleteTeam(action: ReturnType<typeof actions.deleteTeam>) {
  try {
    const response: APIResponse<typeof deleteTeamApi> = yield call(
      deleteTeamApi,
      action.payload.id
    );

    if (response.data.success) {
      yield call(action.payload.onSuccess);
    } else {
      throw new Error(
        `deleteTeam API call returned false, teamId = ${action.payload.id}`
      );
    }
  } catch (e) {
    yield put(handleError(e));
    yield put(
      showToastMessage({
        status: 'error',
        text: TOAST_ERROR_MESSAGE,
      })
    );
  }
}

function* joinTeam(action: ReturnType<typeof actions.joinTeam>) {
  try {
    const response: APIResponse<typeof joinTeamApi> = yield call(
      joinTeamApi,
      action.payload.id
    );

    if (response.data.success) {
      yield call(action.payload.onSuccess);
      yield put(
        showToastMessage({
          status: 'success',
          text: 'Welcome to the team!',
        })
      );
    } else {
      throw new Error(
        `joinTeam API call returned false, teamId = ${action.payload.id}`
      );
    }
  } catch (e) {
    yield put(handleError(e));
    yield put(
      showToastMessage({
        status: 'error',
        text: TOAST_ERROR_MESSAGE,
      })
    );
  }
}

function* leaveTeam(action: ReturnType<typeof actions.leaveTeam>) {
  try {
    const response: APIResponse<typeof leaveTeamApi> = yield call(
      leaveTeamApi,
      action.payload.id
    );

    if (response.data.success) {
      yield call(action.payload.onSuccess);
    } else {
      throw new Error(
        `leaveTeam API call returned false, teamId = ${action.payload.id}`
      );
    }
  } catch (e) {
    yield put(handleError(e));
    yield put(
      showToastMessage({
        status: 'error',
        text: TOAST_ERROR_MESSAGE,
      })
    );
  }
}

function* addUserToTeams(action: ReturnType<typeof actions.addUserToTeams>) {
  try {
    const { userId, teamIds } = action.payload;
    for (let i = 0; i < teamIds.length; i++) {
      yield call(addUserToTeamApi, { teamId: teamIds[i], userId });
    }
    yield put(getTeamsForSelectedUser(userId));
  } catch (e) {
    yield put(handleError(e));
    yield put(
      showToastMessage({
        status: 'error',
        text: TOAST_ERROR_MESSAGE,
      })
    );
  }
}

function* addUsersToTeam(action: ReturnType<typeof actions.addUsersToTeam>) {
  try {
    const { userIds, teamId, onSuccess } = action.payload;
    for (let i = 0; i < userIds.length; i++) {
      yield call(addUserToTeamApi, { teamId, userId: userIds[i] });
    }
    yield call(onSuccess);
  } catch (e) {
    yield put(handleError(e));
    yield put(
      showToastMessage({
        status: 'error',
        text: TOAST_ERROR_MESSAGE,
      })
    );
  }
}

function* removeUserFromTeam(
  action: ReturnType<typeof actions.removeUserFromTeam>
) {
  try {
    const { userId, teamId, onSuccess } = action.payload;
    yield call(removeUserFromTeamApi, { teamId, userId });
    yield call(onSuccess);
  } catch (e) {
    yield put(handleError(e));
    yield put(
      showToastMessage({
        status: 'error',
        text: TOAST_ERROR_MESSAGE,
      })
    );
  }
}

function* issueRequestToJoinTeam(
  action: ReturnType<typeof actions.issueRequestToJoinTeam>
) {
  try {
    const { id, onSuccess } = action.payload;
    yield call(issueRequestToJoinTeamApi, id);

    if (onSuccess) yield call(onSuccess);
    yield put(actions.issueRequestToJoinTeamSucceeded());

    yield put(
      showToastMessage({
        status: 'success',
        text: 'Request sent!',
      })
    );
  } catch (e) {
    yield put(actions.issueRequestToJoinTeamFailed());
    yield put(handleError(e));
    yield put(
      showToastMessage({
        status: 'error',
        text: TOAST_ERROR_MESSAGE,
      })
    );
  }
}

function* getTeamsForProjectCall(
  action: ReturnType<typeof actions.getTeamsForProject>
) {
  try {
    const response: APIResponse<typeof getTeamsForProject> = yield call(
      getTeamsForProject,
      action.payload
    );
    yield put(
      actions.getTeamsForProjectSucceeded({
        teams: getTeamListVM(response.data.results.teams, 'REMOVE'),
        total: response.data.results.total,
      })
    );
  } catch (e) {
    yield put(actions.getTeamsForProjectFailed());
    yield put(handleError(e));
  }
}

function* getProjectsForTeam(
  action: ReturnType<typeof actions.getProjectsForTeam>
) {
  try {
    const { teamId, params } = action.payload;
    const response: APIResponse<typeof getProjectsForTeamApi> = yield call(
      getProjectsForTeamApi,
      teamId,
      params
    );

    yield put(
      actions.getProjectsForTeamSucceeded(
        response.data.results,
        action.payload.params.page > 1
      )
    );
  } catch (e) {
    yield put(actions.getProjectsForTeamFailed());
    yield put(handleError(e));
  }
}

function* getUsersInTeam(action: ReturnType<typeof actions.getUsersInTeam>) {
  try {
    const response: APIResponse<typeof getUsersInTeamApi> = yield call(
      getUsersInTeamApi,
      action.payload
    );

    const { teamAdmins } = yield select(selectTeam);

    yield put(
      actions.getUsersInTeamSucceeded({
        users: getTeamMemberListVM({
          users: response.data.results.users,
          teamAdmins,
        }),
        shouldAppend: action.payload.page > 1,
        total: response.data.results.total,
      })
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getTeamsForPopover(
  action: ReturnType<typeof actions.getTeamsForPopover>
) {
  try {
    const response: APIResponse<typeof getTeamsForPopoverGivenUserApi> = yield call(
      action.payload.userId
        ? getTeamsForPopoverGivenUserApi
        : getTeamsForPopoverGivenClientApi,
      action.payload.userId ?? action.payload.clientId
    );

    yield put(actions.getTeamsForPopoverSucceeded(response.data.teams.teams));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(actionNames.GET_TEAMS, getTeams),
    takeLatest(actionNames.CREATE_TEAM, createTeam),
    takeLatest(actionNames.UPDATE_TEAM, updateTeam),
    takeLatest(actionNames.DELETE_TEAM, deleteTeam),
    takeLatest(actionNames.ADD_USER_TO_TEAMS, addUserToTeams),
    takeLatest(actionNames.ADD_USERS_TO_TEAM, addUsersToTeam),
    takeLatest(actionNames.REMOVE_USER_FROM_TEAM, removeUserFromTeam),
    takeLatest(actionNames.JOIN_TEAM, joinTeam),
    takeLatest(actionNames.ISSUE_REQUEST_TO_JOIN_TEAM, issueRequestToJoinTeam),
    takeLatest(actionNames.LEAVE_TEAM, leaveTeam),
    takeLatest(actionNames.GET_TEAM, getTeam),
    takeLatest(actionNames.GET_TEAMS_FOR_PROJECT, getTeamsForProjectCall),
    takeLatest(actionNames.GET_PROJECTS_FOR_TEAM, getProjectsForTeam),
    takeLatest(actionNames.GET_TEAMS_FOR_POPOVER, getTeamsForPopover),
    takeLatest(actionNames.GET_USERS_IN_TEAM, getUsersInTeam),
  ]);
}

export default saga;
